import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../Auth";
import { endpoints } from "../../endpoints";
import type { CreatedOption, MethodsOfUseForm } from "../../types/types";
import { isCreatedOption } from "../../types/types";
import type {
  AttributeSchema,
  ObjectReferenceResult,
} from "../../types/types.PIM";
import { useStoreState } from "../../util/util";
import type { ChipType } from "../Chips/Chips";
import { DeletableChips } from "../DeletableChips/DeletableChips";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { ENTITY_PERMISSIONS } from "../../pages/admin/SellerAdmin/MDM/shared/utils";
import { getObjectReferencePermissionFlags } from "./utils";
import styled from "styled-components";

export const InputWrapper = styled.div`
  width: 100%;
  max-width: 520px;
  margin-bottom: 10px;
`;

const getObjectReferenceLabel = (item: ObjectReferenceResult) => {
  const details = (item.preview_attributes ?? [])
    .map(({ key, value }) => (value ? `${key}: ${value}` : null))
    .filter(Boolean)
    .join(" | ");
  const defaultDetails = `${item.name} ${
    item.object_number ? `| ID: ${item.object_number}` : ""
  }`;
  if (details) {
    return `${details}`;
  }
  return defaultDetails;
};

const getObjectReferenceChip = (
  value: any
): { name: string; id: string } | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  if (value.object_ref?.id) {
    return {
      name:
        value.object_ref?.name ??
        (typeof value.value === "string"
          ? value.value
          : value.attribute_id ?? ""),
      id: value.object_ref.id,
    };
  }

  if (typeof value.id === "string" && typeof value.name === "string") {
    return { name: value.name, id: value.id };
  }

  if (typeof value.value === "string" && value.value.length > 0) {
    return { name: value.value, id: value.value };
  }

  return null;
};

export function ObjectReferenceField({
  attribute,
  methodsOfUseForm,
  isDisabled,
  isNotApplicable,
  excludeProfileId,
}: {
  attribute: Extract<AttributeSchema, { input_type: "object_reference" }>;
  methodsOfUseForm: MethodsOfUseForm;
  isDisabled?: boolean;
  isNotApplicable: boolean;
  excludeProfileId?: string;
}) {
  const [attributeChips, setAttributeChips] = useState<ChipType[]>([]);
  const [chipsBeforeNotApplicable, setChipsBeforeNotApplicable] =
    useState<ChipType[] | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const objectByIdRef = useRef<Record<string, ObjectReferenceResult>>({});
  const { tenant_id } = useStoreState();
  const { hasPermission } = useAuthContext();
  const { t } = useTranslation();
  const { register, setValue, errors, formState, control } = methodsOfUseForm;

  const getChipName = useCallback(
    (data: CreatedOption) =>
      objectByIdRef.current[String(data.value)]?.name ?? data.label,
    []
  );

  const defaultValues = useMemo(
    () =>
      (attribute.values ?? attribute.default_values ?? [])
        ?.map(getObjectReferenceChip)
        .filter((value): value is { name: string; id: string } =>
          Boolean(value)
        ),
    [attribute.default_values, attribute.values]
  );

  const updateFormValue = useCallback(
    ({
      chips,
      type,
      shouldValidate = false,
    }: {
      chips: ChipType[];
      type: "single_select" | "multi_select";
      shouldValidate?: boolean;
    }) => {
      if (type === "single_select") {
        setValue(
          attribute.id,
          chips.length
            ? { label: chips[0]?.name ?? "", value: chips[0]?.id ?? "" }
            : null,
          { shouldValidate }
        );
      } else {
        setValue(
          attribute.id,
          chips.map(({ id, name }) => ({ label: name, value: id })),
          { shouldValidate }
        );
      }
    },
    [attribute.id, setValue]
  );

  const handleMultiSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      const updated = [
        ...attributeChips,
        { name: getChipName(data), id: data.value },
      ];
      setAttributeChips(updated);
      updateFormValue({
        chips: updated,
        type: "multi_select",
        shouldValidate: true,
      });
    }
  };

  const handleSingleSelectChange = (data: CreatedOption | null) => {
    if (isCreatedOption(data)) {
      const updated = [{ name: getChipName(data), id: data.value }];
      setAttributeChips(updated);
      updateFormValue({
        chips: updated,
        type: "single_select",
        shouldValidate: true,
      });
    } else if (data === null) {
      setAttributeChips([]);
      updateFormValue({
        chips: [],
        type: "single_select",
        shouldValidate: true,
      });
    }
  };

  useEffect(() => {
    if (!isInitialized) {
      register({ name: attribute.id, required: attribute.is_required });
      setIsInitialized(true);
      setAttributeChips(defaultValues);
      updateFormValue({
        chips: defaultValues,
        type: attribute.is_multiple_choice ? "multi_select" : "single_select",
      });
    }
  }, [
    attribute.id,
    attribute.is_multiple_choice,
    attribute.is_required,
    defaultValues,
    isInitialized,
    register,
    updateFormValue,
  ]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (isNotApplicable) {
      if (chipsBeforeNotApplicable === null) {
        setChipsBeforeNotApplicable(attributeChips);
      }

      if (attributeChips.length > 0) {
        setAttributeChips([]);
        updateFormValue({
          chips: [],
          type: attribute.is_multiple_choice ? "multi_select" : "single_select",
        });
      }
      return;
    }

    if (!isNotApplicable && chipsBeforeNotApplicable !== null) {
      const chipsToRestore =
        chipsBeforeNotApplicable && chipsBeforeNotApplicable.length > 0
          ? chipsBeforeNotApplicable
          : defaultValues;
      setAttributeChips(chipsToRestore);
      updateFormValue({
        chips: chipsToRestore,
        type: attribute.is_multiple_choice ? "multi_select" : "single_select",
      });
      setChipsBeforeNotApplicable(null);
    }
  }, [
    attribute.is_multiple_choice,
    attributeChips,
    chipsBeforeNotApplicable,
    defaultValues,
    isInitialized,
    isNotApplicable,
    updateFormValue,
  ]);

  const handleAttributeChipClick = (name: string) => {
    const updated = attributeChips.filter((chip) => chip.name !== name);
    setAttributeChips(updated);
    updateFormValue({
      chips: updated,
      type: attribute.is_multiple_choice ? "multi_select" : "single_select",
      shouldValidate: true,
    });
  };

  const searchParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append("type", attribute.target_object_type ?? "product");
    params.append("order_by", "asc");
    params.append("attribute_id", attribute.id);
    const shouldExcludeCurrentProfile =
      excludeProfileId &&
      attribute.source_object_type &&
      attribute.target_object_type &&
      attribute.source_object_type === attribute.target_object_type;
    if (shouldExcludeCurrentProfile) {
      params.append("exclude_ids", excludeProfileId);
    }
    const permissionSet =
      ENTITY_PERMISSIONS[attribute.target_object_type ?? "product"];
    const { canView, canModify } = getObjectReferencePermissionFlags({
      hasPermission,
      permissionSet,
    });
    if (canView && !canModify) {
      params.append("is_published", "true");
    }
    return params;
  }, [
    attribute.id,
    attribute.source_object_type,
    attribute.target_object_type,
    excludeProfileId,
    hasPermission,
  ]);

  return (
    <div>
      <InputWrapper>
        <Controller
          name={attribute.id}
          control={control}
          key={attribute.id}
          defaultValue={
            defaultValues.length
              ? attribute.is_multiple_choice
                ? defaultValues.map((value) => ({
                    label: value.name,
                    value: value.id,
                  }))
                : { label: defaultValues[0].name, value: defaultValues[0].id }
              : null
          }
          render={({ onChange, ...rest }) => (
            <SearchSelectInfiniteScroll
              {...rest}
              name={attribute.id}
              errors={errors}
              formState={formState}
              isClearable
              disabled={isDisabled || !attribute.is_editable}
              placeholder={
                attribute.display_name
                  ? t([attribute.display_name])
                  : attribute.name
              }
              baseUrl={endpoints.v2_tenants_id_mdm_objects_search(tenant_id)}
              params={searchParams}
              getOptions={(response: ObjectReferenceResult[]) => {
                response.forEach((item) => {
                  objectByIdRef.current[String(item.id)] = item;
                });
                return response
                  .map((item) => ({
                    label: getObjectReferenceLabel(item),
                    value: item.id,
                  }))
                  .filter(
                    (item) =>
                      attributeChips
                        .map((chip) => chip.id)
                        .indexOf(item.value) < 0
                  );
              }}
              onChange={
                attribute.is_multiple_choice
                  ? handleMultiSelectChange
                  : handleSingleSelectChange
              }
              testid={attribute.name}
              autoComplete="on"
              {...(attribute.is_multiple_choice
                ? { value: null }
                : { value: rest.value })}
            />
          )}
        />
      </InputWrapper>
      {attribute.is_multiple_choice && (
        <DeletableChips
          chips={attributeChips}
          noneSelectedName="choices"
          handleClick={handleAttributeChipClick}
        />
      )}
    </div>
  );
}
